/* src/ui/loadingScreen.ts — the boot loading screen (TASKS.md 1.8).
 *
 * Uses the original's non-`gf` class names (`loading-spinner`, `loading-bar`, `loading-eta`,
 * PLAN.md Part 1 §6) on purpose: this screen has to paint before tokens.css and the fonts
 * resolve, so it cannot depend on the design system it sits in front of. Its styles are
 * inlined by src/ui/world.css with literal values for the same reason.
 *
 * The ETA is derived from observed throughput rather than a fixed guess — early in a load
 * there is no throughput to measure, so it stays hidden until there is.
 */

import { on } from '../core/events.ts';
import { el, text } from './dom.ts';

export class LoadingScreen {
  readonly #root: HTMLElement;
  readonly #barFill: HTMLElement;
  readonly #label: Text;
  readonly #eta: Text;
  readonly #startedAt = performance.now();
  #done = false;
  #unsubscribe: (() => void) | null = null;

  constructor() {
    this.#barFill = el('div', { class: 'loading-bar-fill' });
    this.#label = text('Starting up');
    this.#eta = text('');

    this.#root = el(
      'div',
      { class: 'loading-screen', attrs: { role: 'status', 'aria-live': 'polite' } },
      el('div', { class: 'loading-spinner' }),
      el('div', { class: 'loading-text' }, this.#label),
      el('div', { class: 'loading-bar' }, this.#barFill),
      el('div', { class: 'loading-eta' }, this.#eta),
    );
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.#root);
    this.#unsubscribe = on('boot:progress', ({ phase, ratio }) => {
      this.#update(phase, ratio);
    });
  }

  #update(phase: string, ratio: number): void {
    if (this.#done) return;

    const pct = Math.max(0, Math.min(1, ratio));
    this.#barFill.style.width = `${String(Math.round(pct * 100))}%`;
    this.#label.textContent = phase === 'tiles' ? 'Building the city' : phase;

    const elapsed = performance.now() - this.#startedAt;
    // Below 5% the throughput estimate is dominated by startup noise and produces absurd
    // numbers, so no ETA is shown at all rather than a wrong one.
    if (pct > 0.05 && pct < 1) {
      const remaining = (elapsed / pct) * (1 - pct);
      this.#eta.textContent =
        remaining > 1500 ? `about ${String(Math.ceil(remaining / 1000))}s remaining` : 'almost there';
    } else {
      this.#eta.textContent = '';
    }

    if (pct >= 1) this.finish();
  }

  /** Fade out and remove. Idempotent. */
  finish(): void {
    if (this.#done) return;
    this.#done = true;
    this.#unsubscribe?.();
    this.#unsubscribe = null;

    this.#root.classList.add('is-done');
    const remove = (): void => {
      this.#root.remove();
    };
    // Respect reduced motion: no fade, just go (PLAN.md Part 4 §5).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) remove();
    else window.setTimeout(remove, 420);
  }
}
